import { DeleteIconData, PaletteIconData } from "@meiden-connect/material-symbols";
import { Button, Icon } from "@meiden-connect/ui";
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { isValidHexColor, normalizeHexColor } from "../../../lib/hex-color";
import { useCtrlWheelZoom } from "../input/use-ctrl-wheel-zoom";
import { resolveOverlayPosition } from "./overlay-position";
import { SelectionPanelPlacementContext } from "./selection-style-panel-frame";

import {
  ToolbarChildPanelContext,
  ToolbarDismissBoundaryContext,
} from "./toolbar-parent-child-panel";

/**
 * オーバーレイを閉じた理由。
 * `anchor-clipped` は子パネル内のスクロールでアンカーが完全に隠れた場合で、
 * 非表示のアンカーへフォーカスを戻さないよう、呼び出し側の遅延復帰を抑止する。
 */
export type OverlayCloseReason = "dismiss" | "anchor-clipped";

const overlayStyle: CSSProperties = {
  position: "fixed",
  zIndex: 1000,
  maxWidth: "calc(100vw - 16px)",
  maxHeight: "calc(100vh - 16px)",
  overflowY: "auto",
  color: "var(--color-text)",
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius-1)",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.18)",
};

/** アンカーが子パネルの可視領域と 1px 以上交差しなくなったか。 */
function isAnchorClipped(anchor: HTMLElement, container: HTMLElement): boolean {
  const containerRect = container.getBoundingClientRect();
  // 可視領域を実測できない（レイアウト前・非表示）場合は判定材料が無いため、隠れたと決めつけない。
  if (containerRect.width <= 0 || containerRect.height <= 0) return false;
  const anchorRect = anchor.getBoundingClientRect();
  const overlapX =
    Math.min(anchorRect.right, containerRect.right) - Math.max(anchorRect.left, containerRect.left);
  const overlapY =
    Math.min(anchorRect.bottom, containerRect.bottom) - Math.max(anchorRect.top, containerRect.top);
  return overlapX < 1 || overlapY < 1;
}

function useOverlayPosition(
  ref: RefObject<HTMLElement | null>,
  anchorElement: HTMLElement,
  onClose: (reason: OverlayCloseReason) => void
): { readonly left: number; readonly top: number } | null {
  const panelPlacement = useContext(SelectionPanelPlacementContext);
  const childPanel = useContext(ToolbarChildPanelContext);
  const childPlacement = childPanel?.placement ?? null;
  const scrollContainer = childPanel?.scrollContainer ?? null;
  const [position, setPosition] = useState<{ readonly left: number; readonly top: number } | null>(
    null
  );

  const reposition = useCallback((): void => {
    const element = ref.current;
    if (!element) return;
    const size = element.getBoundingClientRect();
    const next = resolveOverlayPosition(
      anchorElement.getBoundingClientRect(),
      { width: size.width, height: size.height },
      { width: window.innerWidth, height: window.innerHeight }
    );
    setPosition((previous) =>
      previous?.left === next.left && previous.top === next.top ? previous : next
    );
  }, [anchorElement, ref]);

  /**
   * アンカーがまだ子の可視領域に残っていれば追従し、完全に隠れていれば overlay だけを閉じる。
   * 子内部のスクロールだけでなく、子の反転・clamp・実寸変化でもアンカーは隠れ得るため、
   * どちらの契機からも同じ判定を通す。
   */
  const settle = useCallback((): void => {
    if (scrollContainer && isAnchorClipped(anchorElement, scrollContainer)) {
      const element = ref.current;
      // 閉じるオーバーレイ内にフォーカスがあった場合だけ、子のスクロールコンテナへ退避する。
      if (element?.contains(document.activeElement)) {
        scrollContainer.focus({ preventScroll: true });
      }
      onClose("anchor-clipped");
      return;
    }
    reposition();
  }, [anchorElement, onClose, ref, reposition, scrollContainer]);

  // 親パネルの移動と、子パネルの反転・clamp・実寸変化のどちらでもアンカー実矩形を読み直す。
  useLayoutEffect(settle, [settle, panelPlacement, childPlacement]);

  useEffect(() => {
    if (!scrollContainer) return undefined;
    let frame = 0;
    const onScroll = (): void => {
      // スクロールごとの再計測は requestAnimationFrame 単位でまとめ、親の配置 state は更新しない。
      if (frame !== 0) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        settle();
      });
    };
    // 子の内側に入れ子のスクロール領域があっても拾えるよう capture phase で購読する。
    scrollContainer.addEventListener("scroll", onScroll, true);
    return () => {
      scrollContainer.removeEventListener("scroll", onScroll, true);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, [scrollContainer, settle]);

  return position;
}

function useOverlayDismiss(ref: RefObject<HTMLElement | null>, onClose: () => void): void {
  const portalRoots = useContext(ToolbarDismissBoundaryContext);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!portalRoots || !element) return;
    portalRoots.add(element);
    return () => { portalRoots.delete(element); };
  }, [portalRoots, ref]);
  useEffect(() => {
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (target instanceof Node && ref.current?.contains(target)) return;
      // 下書きを閉じるクリックで、キャンバス操作まで同時に起こさない。
      event.stopPropagation();
      onClose();
    };
    const onResize = (): void => onClose();
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("resize", onResize);
    };
  }, [onClose, ref]);
}

function trapFocus(
  event: ReactKeyboardEvent<HTMLElement>,
  focusables: readonly RefObject<HTMLElement | null>[]
): void {
  if (event.key !== "Tab") return;
  const elements = focusables.flatMap((ref) => (ref.current ? [ref.current] : []));
  if (elements.length === 0) return;
  const activeIndex = elements.indexOf(document.activeElement as HTMLElement);
  const nextIndex = event.shiftKey
    ? activeIndex <= 0
      ? elements.length - 1
      : activeIndex - 1
    : activeIndex < 0 || activeIndex === elements.length - 1
      ? 0
      : activeIndex + 1;
  event.preventDefault();
  elements[nextIndex]?.focus();
}

export interface CustomColorPopoverProps {
  readonly mode: "add" | "edit";
  readonly initialColor: string;
  readonly anchorElement: HTMLElement;
  readonly onCommit: (color: string) => void;
  readonly onCancel: (reason: OverlayCloseReason) => void;
}

export function CustomColorPopover({
  mode,
  initialColor,
  anchorElement,
  onCommit,
  onCancel,
}: CustomColorPopoverProps): JSX.Element {
  const normalizedInitial = normalizeHexColor(initialColor);
  const [draft, setDraft] = useState(normalizedInitial);
  const [lastValidColor, setLastValidColor] = useState(normalizedInitial);
  const committedRef = useRef(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const pickerRef = useRef<HTMLInputElement | null>(null);
  const hexRef = useRef<HTMLInputElement | null>(null);
  const commitRef = useRef<HTMLButtonElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const closeWithReason = useCallback(
    (reason: OverlayCloseReason) => {
      if (!committedRef.current) onCancel(reason);
    },
    [onCancel]
  );
  const position = useOverlayPosition(rootRef, anchorElement, closeWithReason);
  useCtrlWheelZoom(rootRef);

  const cancel = useCallback(() => closeWithReason("dismiss"), [closeWithReason]);
  useOverlayDismiss(rootRef, cancel);

  useEffect(() => {
    hexRef.current?.focus();
  }, []);

  const valid = isValidHexColor(draft.trim());
  const commit = useCallback(() => {
    if (committedRef.current || !isValidHexColor(draft.trim())) return;
    committedRef.current = true;
    onCommit(normalizeHexColor(draft));
  }, [draft, onCommit]);

  const onRootKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    event.stopPropagation();
    trapFocus(event, [pickerRef, hexRef, commitRef, cancelRef]);
    if (event.key === "Escape" && !event.nativeEvent.isComposing) {
      event.preventDefault();
      cancel();
    }
  };

  return createPortal(
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label={mode === "add" ? "カスタム色を追加" : "カスタム色を変更"}
      data-testid="custom-color-popover"
      onKeyDown={onRootKeyDown}
      style={{
        ...overlayStyle,
        left: position?.left ?? 0,
        top: position?.top ?? 0,
        visibility: position ? "visible" : "hidden",
        width: "280px",
        padding: "12px",
        display: "grid",
        gap: "10px",
      }}
    >
      <strong>{mode === "add" ? "カスタム色を追加" : "カスタム色を変更"}</strong>
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <input
          ref={pickerRef}
          type="color"
          aria-label="色を選ぶ"
          data-testid="custom-color-picker"
          value={lastValidColor}
          onChange={(event) => {
            const nextValue = event.currentTarget.value;
            setDraft(nextValue);
            if (isValidHexColor(nextValue)) setLastValidColor(normalizeHexColor(nextValue));
          }}
        />
        <input
          ref={hexRef}
          type="text"
          aria-label="カラーコード（#RRGGBB または #RGB）"
          placeholder="#RRGGBB"
          data-testid="custom-color-hex"
          value={draft}
          onChange={(event) => {
            const nextValue = event.currentTarget.value;
            setDraft(nextValue);
            if (isValidHexColor(nextValue.trim())) {
              setLastValidColor(normalizeHexColor(nextValue));
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.stopPropagation();
              if (!event.nativeEvent.isComposing) {
                event.preventDefault();
                commit();
              }
            }
          }}
          style={{ minWidth: 0, flex: 1 }}
        />
      </div>
      <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", flexWrap: "wrap" }}>
        <Button ref={commitRef} type="button" variant="action" disabled={!valid} onClick={commit}>
          {mode === "add" ? "追加" : "保存"}
        </Button>
        <Button ref={cancelRef} type="button" variant="action" onClick={cancel}>
          キャンセル
        </Button>
      </div>
    </div>,
    document.body
  );
}

export interface CustomColorMenuProps {
  readonly color: string;
  readonly anchorElement: HTMLElement;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
  readonly onClose: (reason: OverlayCloseReason) => void;
}

export function CustomColorMenu({
  color: _color,
  anchorElement,
  onEdit,
  onDelete,
  onClose,
}: CustomColorMenuProps): JSX.Element {
  const rootRef = useRef<HTMLUListElement | null>(null);
  const editRef = useRef<HTMLButtonElement | null>(null);
  const deleteRef = useRef<HTMLButtonElement | null>(null);
  const position = useOverlayPosition(rootRef, anchorElement, onClose);
  const dismiss = useCallback(() => onClose("dismiss"), [onClose]);
  useCtrlWheelZoom(rootRef);
  useOverlayDismiss(rootRef, dismiss);
  useEffect(() => {
    editRef.current?.focus();
  }, []);

  const menuItem = (
    label: string,
    icon: string,
    danger: boolean,
    ref: RefObject<HTMLButtonElement | null>,
    onClick: () => void
  ): JSX.Element => (
    <li role="none" style={{ margin: 0 }}>
      <button
        ref={ref}
        role="menuitem"
        type="button"
        onClick={onClick}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          width: "100%",
          padding: "8px 12px",
          background: "transparent",
          border: "none",
          borderRadius: "var(--radius-1)",
          textAlign: "left",
          cursor: "pointer",
          color: danger ? "#d22b2b" : "inherit",
          font: "inherit",
        }}
        onMouseEnter={(event) => {
          event.currentTarget.style.background = "var(--color-surface-muted)";
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.background = "transparent";
        }}
      >
        <Icon d={icon} />
        <span>{label}</span>
      </button>
    </li>
  );

  return createPortal(
    <ul
      ref={rootRef}
      role="menu"
      aria-label="カスタム色の操作"
      data-testid="custom-color-menu"
      onKeyDown={(event) => {
        event.stopPropagation();
        trapFocus(event, [editRef, deleteRef]);
        if (event.key === "Escape" && !event.nativeEvent.isComposing) {
          event.preventDefault();
          dismiss();
        }
      }}
      style={{
        ...overlayStyle,
        left: position?.left ?? 0,
        top: position?.top ?? 0,
        visibility: position ? "visible" : "hidden",
        width: "160px",
        margin: 0,
        padding: "4px",
        listStyle: "none",
      }}
    >
      {menuItem("色を変更", PaletteIconData, false, editRef, onEdit)}
      {menuItem("色を削除", DeleteIconData, true, deleteRef, onDelete)}
    </ul>,
    document.body
  );
}
